// Dashboard - Page d'accueil Cockpit Particulier (Style HeyGen)
// Structure: Salutation + Bannière Actualités + 4 Cards en grille 2x2

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Wrench, ShoppingCart, Briefcase, Globe, Newspaper, ChevronRight
} from 'lucide-react';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import RegisterPromptModal from '../../components/RegisterPromptModal';

// Action Card Component
function ActionCard({ icon: Icon, subtitle, title, onClick, testId }) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      data-testid={testId}
      className="bg-white rounded-2xl p-4 text-left w-full"
      style={{
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        minHeight: '120px',
      }}
    >
      {/* Icône */}
      <div 
        className="w-8 h-8 rounded-lg flex items-center justify-center mb-3"
        style={{ backgroundColor: 'rgba(232, 101, 10, 0.1)' }}
      >
        <Icon size={20} style={{ color: '#E8650A' }} />
      </div>
      
      {/* Sous-titre */}
      <p 
        className="uppercase tracking-wide"
        style={{
          fontSize: '11px',
          fontWeight: 500,
          color: '#888888',
          marginTop: '12px',
          lineHeight: 1.3,
        }}
      >
        {subtitle}
      </p>
      
      {/* Titre */}
      <h3 
        style={{
          fontSize: '15px',
          fontWeight: 700,
          color: '#1a2a4a',
          marginTop: '4px',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}
      >
        {title}
      </h3>
    </motion.button>
  );
}

export default function DashboardPage() {
  const { user, isLoading } = useCurrentUser();
  const navigate = useNavigate();
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);

  // Récupérer le prénom de l'utilisateur
  const firstName = user?.first_name || user?.name?.split(' ')[0] || '';

  // Handlers pour les 4 actions
  const handlePrestataires = () => {
    navigate('/artisans');
  };

  const handleMarketplace = () => {
    navigate('/marketplace');
  };

  const handleEmploi = () => {
    navigate('/emploi');
  };

  const handleReseau = () => {
    navigate('/reseau');
  };

  // Handler pour la bannière Actualités
  const handleActualites = () => {
    // Simuler un clic sur l'onglet "Actu" de la bottom nav
    // En réalité, on redirige vers la page du feed/actualités
    navigate('/cockpit-particulier/feed');
  };

  return (
    <div 
      className="min-h-screen pb-24"
      style={{ backgroundColor: '#f8f9fa' }}
    >
      {/* Register Prompt Modal */}
      <RegisterPromptModal 
        isOpen={showRegisterPrompt}
        onClose={() => setShowRegisterPrompt(false)}
        actionLabel="cette fonctionnalité"
      />

      {/* ═══════════════════════════════════════════════════════════════
          BLOC 1 — SALUTATION (style HeyGen)
      ═══════════════════════════════════════════════════════════════ */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-5 pt-6"
      >
        {/* Ligne 1: Bonjour [Prénom], */}
        <h1 
          style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: '28px',
            fontWeight: 700,
            color: '#1a2a4a',
            lineHeight: 1.2,
          }}
        >
          Bonjour{firstName ? ` ${firstName}` : ''},
        </h1>
        
        {/* Ligne 2: Par où veux-tu commencer ? */}
        <p 
          style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: '20px',
            fontWeight: 500,
            color: 'rgba(26, 42, 74, 0.7)',
            marginTop: '4px',
          }}
        >
          Par où veux-tu commencer ?
        </p>
      </motion.div>

      {/* ═══════════════════════════════════════════════════════════════
          BLOC 2 — BANNIÈRE FINE ACTUALITÉS
      ═══════════════════════════════════════════════════════════════ */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="px-5 mt-4"
      >
        <button
          onClick={handleActualites}
          className="w-full flex items-center justify-between px-4 bg-white active:bg-gray-50 transition-colors"
          style={{
            height: '48px',
            borderRadius: '12px',
            border: '1px solid #fce4d4',
          }}
        >
          <div className="flex items-center gap-2">
            <Newspaper size={18} style={{ color: '#E8650A' }} />
            <span 
              style={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#1a2a4a',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              Voir les actualités de mes proches
            </span>
          </div>
          <ChevronRight size={18} style={{ color: '#1a2a4a', opacity: 0.5 }} />
        </button>
      </motion.div>

      {/* ═══════════════════════════════════════════════════════════════
          BLOC 3 — 4 CARDS EN GRILLE 2x2
      ═══════════════════════════════════════════════════════════════ */}
      <div 
        className="px-5 mt-5"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '12px',
        }}
      >
        {/* Card 1 — Prestataires de services */}
        <ActionCard
          icon={Wrench}
          subtitle="TROUVER"
          title="Prestataires de services"
          onClick={handlePrestataires}
          testId="card-prestataires"
        />

        {/* Card 2 — Marketplace */}
        <ActionCard
          icon={ShoppingCart}
          subtitle="ACHETER · VENDRE · LOUER"
          title="Marketplace"
          onClick={handleMarketplace}
          testId="card-marketplace"
        />

        {/* Card 3 — Emploi */}
        <ActionCard
          icon={Briefcase}
          subtitle="POSTULER · RECRUTER"
          title="Emploi"
          onClick={handleEmploi}
          testId="card-emploi"
        />

        {/* Card 4 — Réseau Social */}
        <ActionCard
          icon={Globe}
          subtitle="DÉCOUVRIR · PARTAGER"
          title="Réseau Social"
          onClick={handleReseau}
          testId="card-reseau"
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          STYLES ADAPTATIFS (déjà gérés par Tailwind + inline styles)
          - 360px: gap réduit, padding réduit
          - 390px: gap 12px, padding 20px (cible principale)
          - 428px+: gap 14px, padding 24px
          - 768px+: max-width 480px, centré
      ═══════════════════════════════════════════════════════════════ */}
      <style>{`
        @media (max-width: 360px) {
          .px-5 { padding-left: 16px !important; padding-right: 16px !important; }
        }
        @media (min-width: 428px) {
          .px-5 { padding-left: 24px !important; padding-right: 24px !important; }
        }
        @media (min-width: 768px) {
          .min-h-screen > div:last-of-type {
            max-width: 480px;
            margin-left: auto;
            margin-right: auto;
          }
        }
      `}</style>
    </div>
  );
}
